import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
    Leaf,
    ArrowLeft,
    MapPin,
    Info,
    Lightbulb
} from '@phosphor-icons/react';

export default function AnimalDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { api } = useAuth();
    const [animal, setAnimal] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchAnimal();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const fetchAnimal = async () => {
        try {
            const response = await api().get(`/animals/${id}`);
            setAnimal(response.data);
        } catch (error) {
            toast.error('Failed to load animal');
            navigate('/animals');
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (status) => {
        const statusLower = status.toLowerCase();
        if (statusLower.includes('vulnerable') || statusLower.includes('endangered')) {
            return 'bg-[#fb7185] text-white';
        }
        if (statusLower.includes('near threatened')) {
            return 'bg-[#fde047] text-[#1e293b]';
        }
        return 'bg-[#22c55e] text-white';
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[hsl(53,92%,96%)] flex items-center justify-center">
                <div className="brutalist-card p-8 text-center">
                    <div className="w-16 h-16 border-4 border-[#22c55e] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="font-fredoka text-lg">Loading...</p>
                </div>
            </div>
        );
    }

    if (!animal) return null;

    return (
        <div className="min-h-screen bg-[hsl(53,92%,96%)] dot-grid">
            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b-4 border-[#1e293b]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
                            <div className="w-10 h-10 rounded-full bg-[#22c55e] border-4 border-[#1e293b] flex items-center justify-center">
                                <Leaf weight="fill" className="text-white" size={20} />
                            </div>
                            <span className="font-fredoka text-xl font-bold text-[#1e293b] hidden sm:block">
                                Bush Rangers
                            </span>
                        </Link>
                        
                        <Link 
                            to="/animals"
                            className="flex items-center gap-2 text-[#475569] hover:text-[#22c55e] font-semibold"
                            data-testid="back-to-animals"
                        >
                            <ArrowLeft weight="bold" size={20} />
                            <span className="hidden sm:inline">Back to Animals</span>
                        </Link>
                    </div>
                </div>
            </nav>

            {/* Main Content */}
            <main className="pt-20 px-4 pb-8 max-w-4xl mx-auto">
                {/* Hero Image */}
                <motion.div 
                    className="brutalist-card overflow-hidden mb-8"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                >
                    <div className="h-64 md:h-96 relative">
                        <img 
                            src={animal.image_url} 
                            alt={animal.name}
                            crossOrigin="anonymous"
                            referrerPolicy="no-referrer"
                            className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                        <div className="absolute bottom-0 left-0 right-0 p-6">
                            <div className="flex flex-wrap gap-2 mb-3">
                                <span className={`px-3 py-1 rounded-full text-sm font-bold border-2 border-white ${getStatusColor(animal.conservation_status)}`}>
                                    {animal.conservation_status}
                                </span>
                                <span className={`px-3 py-1 rounded-full text-sm font-bold border-2 border-white text-white capitalize ${
                                    animal.ecosystem === 'bush' ? 'bg-[#22c55e]' :
                                    animal.ecosystem === 'reef' ? 'bg-[#38bdf8]' :
                                    'bg-[#f59e0b]'
                                }`}>
                                    {animal.ecosystem}
                                </span>
                            </div>
                            <h1 className="font-fredoka text-3xl md:text-4xl font-bold text-white mb-1">
                                {animal.name}
                            </h1>
                            <p className="text-white/80 italic text-lg">{animal.species}</p>
                        </div>
                    </div>
                </motion.div>

                <div className="grid md:grid-cols-3 gap-6">
                    {/* Main Content */}
                    <motion.div 
                        className="md:col-span-2 space-y-6"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                    >
                        {/* About */}
                        <div className="brutalist-card p-6">
                            <h2 className="font-fredoka text-xl font-bold text-[#1e293b] mb-3 flex items-center gap-2">
                                <Info weight="duotone" className="text-[#38bdf8]" size={24} />
                                About
                            </h2>
                            <p className="text-[#475569] leading-relaxed">
                                {animal.description}
                            </p>
                        </div>

                        {/* Fun Facts */}
                        <div className="brutalist-card p-6 bg-[#fde047]/20">
                            <h2 className="font-fredoka text-xl font-bold text-[#1e293b] mb-4 flex items-center gap-2">
                                <Lightbulb weight="duotone" className="text-[#f59e0b]" size={24} />
                                Fun Facts!
                            </h2>
                            <div className="space-y-3">
                                {animal.fun_facts.map((fact, index) => (
                                    <motion.div
                                        key={index}
                                        initial={{ opacity: 0, x: -20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: 0.1 * index }}
                                        className="flex items-start gap-3 bg-white p-4 rounded-xl border-4 border-[#1e293b] shadow-[2px_2px_0_0_#1e293b]"
                                    >
                                        <span className="text-2xl">💡</span>
                                        <p className="text-[#1e293b] font-semibold">{fact}</p>
                                    </motion.div>
                                ))}
                            </div>
                        </div>
                    </motion.div>

                    {/* Sidebar */}
                    <motion.div 
                        className="space-y-6"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                    >
                        {/* Habitat */}
                        <div className="brutalist-card p-6">
                            <h3 className="font-fredoka font-bold text-[#1e293b] mb-3 flex items-center gap-2">
                                <MapPin weight="duotone" className="text-[#22c55e]" size={20} />
                                Habitat
                            </h3>
                            <p className="text-[#475569]">{animal.habitat}</p>
                        </div>

                        {/* Conservation Status */}
                        <div className="brutalist-card p-6">
                            <h3 className="font-fredoka font-bold text-[#1e293b] mb-3">
                                Conservation Status
                            </h3>
                            <div className={`px-4 py-3 rounded-xl text-center font-bold ${getStatusColor(animal.conservation_status)} border-4 border-[#1e293b]`}>
                                {animal.conservation_status}
                            </div>
                            <p className="text-sm text-[#475569] mt-3">
                                {animal.conservation_status.toLowerCase().includes('vulnerable') 
                                    ? "This species needs our help! Complete missions to protect their habitat."
                                    : animal.conservation_status.toLowerCase().includes('near threatened')
                                    ? "This species is at risk. Let's work together to keep them safe!"
                                    : "This species is doing well, but we still need to protect their home!"}
                            </p>
                        </div>

                        {/* Help This Animal */}
                        <div className="brutalist-card p-6 bg-[#22c55e]">
                            <h3 className="font-fredoka font-bold text-white mb-3">
                                Help {animal.name.split(' ')[0]}!
                            </h3>
                            <p className="text-white/90 text-sm mb-4">
                                Complete missions to help protect {animal.name.split(' ')[0]}'s habitat and friends.
                            </p>
                            <Link to={`/missions?ecosystem=${animal.ecosystem}`} data-testid="related-missions-link">
                                <motion.button
                                    whileHover={{ y: -2, x: -2 }}
                                    whileTap={{ y: 2, x: 2 }}
                                    className="brutalist-btn w-full bg-white text-[#22c55e]"
                                >
                                    Find Missions
                                </motion.button>
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </main>
        </div>
    );
}
